import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Group,
  PasswordInput,
  Radio,
  Stack,
  Stepper,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useLocalAuth } from '../auth/local-auth';
import type { PlanCode, SignupTenantInput } from '../auth/local-auth';
import { Brand } from '../components/Brand';
import { CONDICIONES_URL, CONDICIONES_VERSION } from '../legal/condiciones';

/**
 * Plan comercial del SaaS. Coincide con la landing (`/precio`) y con
 * el enum `PlanCode` del backend.
 */
const PLANES: Array<{
  code: PlanCode;
  nombre: string;
  precioLabel: string;
  limiteDecasMes: number;
  descripcion: string;
  destacado?: boolean;
}> = [
  {
    code: 'gratis',
    nombre: 'Gratis',
    precioLabel: '0 €/mes',
    limiteDecasMes: 10,
    descripcion: '10 DeCAs al mes. Para probar el producto sin compromiso.',
  },
  {
    code: 'simple',
    nombre: 'Simple',
    precioLabel: '5,95 €/mes',
    limiteDecasMes: 100,
    descripcion: '100 DeCAs al mes. Para autónomos con volumen medio.',
    destacado: true,
  },
  {
    code: 'todo',
    nombre: 'Todo',
    precioLabel: '11,95 €/mes',
    limiteDecasMes: 300,
    descripcion: '300 DeCAs al mes. Para flotas pequeñas y gestorías.',
  },
];

/** Valida query param `plan` y devuelve el codigo normalizado o undefined. */
function planFromSearch(search: URLSearchParams): PlanCode | undefined {
  const raw = search.get('plan');
  if (raw === 'gratis' || raw === 'simple' || raw === 'todo') return raw;
  return undefined;
}

export default function Signup() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const { signup } = useLocalAuth();
  const [active, setActive] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const planInicial = planFromSearch(search) ?? 'gratis';

  const form = useForm({
    initialValues: {
      name: '',
      email: '',
      password: '',
      passwordConfirm: '',
      tenant: {
        razonSocial: '',
        nifCif: '',
        direccion: '',
        municipio: '',
        codigoPostal: '',
        provincia: '',
        telefono: '',
        pais: 'ES',
      } satisfies SignupTenantInput,
      plan: planInicial as PlanCode,
      aceptaCondiciones: false,
    },
    validate: {
      name: (v) => (v.trim().length < 2 ? 'Introduce tu nombre (mín. 2 letras)' : null),
      email: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : 'Email no válido'),
      password: (v) => (v.length < 8 ? 'Mínimo 8 caracteres' : null),
      passwordConfirm: (v, values) =>
        v !== values.password ? 'Las contraseñas no coinciden' : null,
      tenant: {
        razonSocial: (v) => (!v.trim() ? 'Obligatorio' : null),
        nifCif: (v) => (!v.trim() ? 'Obligatorio' : null),
        // Direccion, CP, poblacion, provincia y telefono son OPCIONALES.
        // Si el usuario los rellena, exigimos formato valido en CP.
        codigoPostal: (v) => {
          const t = v.trim();
          if (!t) return null;
          return /^\d{4,5}$/.test(t) ? null : 'CP de 4 o 5 dígitos';
        },
      },
    },
  });

  // Si el query param `plan` cambia (p.ej. el usuario abre dos pestañas
  // con links distintos), sincronizamos el plan seleccionado.
  useEffect(() => {
    const fromUrl = planFromSearch(search);
    if (fromUrl && fromUrl !== form.values.plan) {
      form.setFieldValue('plan', fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const next = () => {
    // Validacion por paso para no dejar avanzar con campos vacios.
    if (active === 0) {
      const checks = [
        form.validateField('name'),
        form.validateField('email'),
        form.validateField('password'),
        form.validateField('passwordConfirm'),
      ];
      if (checks.some((r) => r.hasError)) return;
    } else if (active === 1) {
      // Solo razon social y CIF/NIF son obligatorios. Direccion, CP,
      // poblacion, provincia y telefono son opcionales; aun asi pasamos
      // el validador de CP para que, si el usuario escribe algo, tenga
      // formato correcto (4 o 5 digitos).
      // `validateField` devuelve { hasError, error } por campo. Asi evitamos
      // depender del tipado de `form.errors.tenant.*` (Mantine lo infiere
      // como ReactNode cuando el campo no tiene error, lo que rompe la
      // comparación booleana).
      const checks = [
        form.validateField('tenant.razonSocial'),
        form.validateField('tenant.nifCif'),
        form.validateField('tenant.codigoPostal'),
      ];
      if (checks.some((r) => r.hasError)) return;
    }
    setActive((s) => Math.min(s + 1, 2));
  };
  const prev = () => setActive((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitError(null);
    if (!form.values.plan) {
      setSubmitError('Elige un plan para continuar');
      return;
    }
    if (!form.values.aceptaCondiciones) {
      setSubmitError('Debes aceptar las Condiciones del servicio para crear la cuenta');
      return;
    }
    setSubmitting(true);
    try {
      await signup({
        email: form.values.email.trim().toLowerCase(),
        password: form.values.password,
        name: form.values.name.trim(),
        plan: form.values.plan,
        tenant: {
          razonSocial: form.values.tenant.razonSocial.trim(),
          nifCif: form.values.tenant.nifCif.trim(),
          // Direccion, CP, poblacion, provincia y telefono son OPCIONALES.
          // Enviamos undefined si estan vacios para que el backend no los
          // persista como strings vacios en el Tenant.
          direccion: form.values.tenant.direccion.trim() || undefined,
          municipio: form.values.tenant.municipio.trim() || undefined,
          codigoPostal: form.values.tenant.codigoPostal.trim() || undefined,
          provincia: form.values.tenant.provincia.trim() || undefined,
          telefono: form.values.tenant.telefono.trim() || undefined,
          pais: form.values.tenant.pais || 'ES',
        },
        aceptaCondiciones: true,
        condicionesVersion: CONDICIONES_VERSION,
      });
      // Tras signup, llevamos al user a la pantalla "revisa tu email".
      // El email va en location.state para que el boton de reenviar
      // sepa a que direccion mandar el nuevo enlace. La pantalla
      // /verificar-email se sigue usando cuando el user pulsa el enlace
      // del email (llega con ?token=xxx).
      navigate('/verifica-tu-email', {
        replace: true,
        state: { email: form.values.email.trim().toLowerCase() },
      });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Error al crear la cuenta');
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Center mih="100vh" p="md" bg="gray.0">
      <Stack gap="lg" w="100%" maw={520}>
        <Stack gap="xs" align="center">
          <Brand />
          <Title order={2}>Crea tu cuenta</Title>
          <Text c="dimmed" size="sm" ta="center">
            3 pasos. Sin tarjeta. Cancela cuando quieras.
          </Text>
        </Stack>

        <Card withBorder shadow="sm" padding="xl" radius="md">
          <Stepper active={active} onStepClick={setActive} size="sm" color="deca">
            {/* ─── Paso 1: Cuenta ─── */}
            <Stepper.Step label="Cuenta" description="Tu acceso al panel">
              <Stack gap="md" mt="md">
                <TextInput
                  label="Nombre"
                  placeholder="Tu nombre"
                  withAsterisk
                  {...form.getInputProps('name')}
                />
                <TextInput
                  label="Email"
                  placeholder="tu@empresa.com"
                  type="email"
                  withAsterisk
                  {...form.getInputProps('email')}
                />
                <PasswordInput
                  label="Contraseña"
                  placeholder="Mínimo 8 caracteres"
                  withAsterisk
                  {...form.getInputProps('password')}
                />
                <PasswordInput
                  label="Repetir contraseña"
                  placeholder="Escribe la contraseña otra vez"
                  withAsterisk
                  {...form.getInputProps('passwordConfirm')}
                />
              </Stack>
            </Stepper.Step>

            {/* ─── Paso 2: Datos del cliente (Tenant) ─── */}
            <Stepper.Step label="Cliente" description="Datos fiscales">
              <Text size="sm" c="dimmed" mt="md" mb="xs">
                Estos datos son del titular de la cuenta (a quién facturamos). Tus transportistas y
                cargadores los das de alta por separado al terminar el registro.
              </Text>
              <Text size="xs" c="dimmed" mb="xs">
                Solo la razón social y el CIF/NIF son obligatorios. El resto lo puedes rellenar
                ahora o mas tarde desde Configuracion.
              </Text>
              <Stack gap="md">
                <TextInput
                  label="Razón social"
                  placeholder="Transportes López S.L."
                  withAsterisk
                  {...form.getInputProps('tenant.razonSocial')}
                />
                <TextInput
                  label="CIF / NIF"
                  placeholder="B12345678"
                  withAsterisk
                  {...form.getInputProps('tenant.nifCif')}
                  onChange={(e) => {
                    form.setFieldValue('tenant.nifCif', e.currentTarget.value.toUpperCase());
                  }}
                />
                <TextInput
                  label="Dirección"
                  placeholder="Calle Mayor 1"
                  {...form.getInputProps('tenant.direccion')}
                />
                <Group grow>
                  <TextInput
                    label="Código postal"
                    placeholder="28013"
                    {...form.getInputProps('tenant.codigoPostal')}
                  />
                  <TextInput
                    label="Población"
                    placeholder="Madrid"
                    {...form.getInputProps('tenant.municipio')}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Provincia"
                    placeholder="Madrid"
                    {...form.getInputProps('tenant.provincia')}
                  />
                  <TextInput
                    label="Teléfono"
                    placeholder="+34 612 345 678"
                    {...form.getInputProps('tenant.telefono')}
                  />
                </Group>
              </Stack>
            </Stepper.Step>

            {/* ─── Paso 3: Plan ─── */}
            <Stepper.Step label="Plan" description="Elige tu plan">
              <Radio.Group
                value={form.values.plan}
                onChange={(v) => form.setFieldValue('plan', v as PlanCode)}
              >
                <Stack gap="sm" mt="md">
                  {PLANES.map((p) => (
                    <PlanCard
                      key={p.code}
                      plan={p}
                      selected={form.values.plan === p.code}
                      onSelect={() => form.setFieldValue('plan', p.code)}
                    />
                  ))}
                </Stack>
              </Radio.Group>
              <Checkbox
                mt="lg"
                color="deca"
                checked={form.values.aceptaCondiciones}
                onChange={(e) => form.setFieldValue('aceptaCondiciones', e.currentTarget.checked)}
                label={
                  <>
                    He leído y acepto las{' '}
                    <Anchor href={CONDICIONES_URL} target="_blank" rel="noopener noreferrer">
                      Condiciones del servicio
                    </Anchor>
                    , incluida la limitación de responsabilidad.
                  </>
                }
              />
              <Text size="xs" c="dimmed" mt={6} ml={32}>
                decaPlus es una herramienta de apoyo: la revisión de cada DeCA y el cumplimiento de
                la normativa de transporte siguen siendo responsabilidad de tu empresa.
              </Text>
            </Stepper.Step>

            <Stepper.Completed>
              <Text ta="center" mt="xl">
                Listo. Pulsa &quot;Crear cuenta&quot; para terminar.
              </Text>
            </Stepper.Completed>
          </Stepper>

          {submitError && (
            <Text c="red" size="sm" mt="sm" ta="center">
              {submitError}
            </Text>
          )}

          <Group justify="space-between" mt="xl">
            <Button variant="default" onClick={prev} disabled={active === 0 || submitting}>
              Atrás
            </Button>
            {active < 2 ? (
              <Button color="deca" onClick={next}>
                Siguiente
              </Button>
            ) : (
              <Button
                color="deca"
                onClick={submit}
                loading={submitting}
                disabled={!form.values.plan || !form.values.aceptaCondiciones}
              >
                Crear cuenta
              </Button>
            )}
          </Group>
        </Card>

        <Text size="sm" c="dimmed" ta="center">
          ¿Ya tienes cuenta?{' '}
          <Anchor component={Link} to="/login">
            Inicia sesión
          </Anchor>
        </Text>
      </Stack>
    </Center>
  );
}

function PlanCard({
  plan,
  selected,
  onSelect,
}: {
  plan: (typeof PLANES)[number];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <UnstyledButton onClick={onSelect} w="100%">
      <Card
        withBorder
        padding="md"
        radius="md"
        style={{
          borderColor: selected ? 'var(--mantine-color-deca-6)' : undefined,
          borderWidth: selected ? 2 : 1,
          backgroundColor: selected ? 'var(--mantine-color-deca-0)' : undefined,
        }}
      >
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Group gap="sm" align="flex-start">
            <Radio checked={selected} onChange={onSelect} />
            <Box>
              <Group gap="xs" align="baseline">
                <Text fw={600}>{plan.nombre}</Text>
                {plan.destacado && (
                  <Text size="xs" c="deca" fw={600}>
                    MÁS ELEGIDO
                  </Text>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                {plan.descripcion}
              </Text>
            </Box>
          </Group>
          <Stack gap={2} align="flex-end">
            <Text fw={700}>{plan.precioLabel}</Text>
            <Text size="xs" c="dimmed">
              {plan.limiteDecasMes} DeCAs/mes
            </Text>
          </Stack>
        </Group>
      </Card>
    </UnstyledButton>
  );
}
